/**
 * NewTripPage · v2.7.65+E143 (2026-02-03)
 * ═════════════════════════════════════════════════════════════════════
 * Upfront dispatch flow that replaces the deprecated Live Meter tiles.
 * Drivers punch in passenger + destination info, the server returns a
 * live quote, and hitting "Create Trip" persists it via the existing
 * POST /api/trips endpoint (dispatch + accounting pipelines untouched).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, Minus, Save, Calculator, MapPin, User, ArrowRight, Printer, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtUsd } from "@/lib/format";

const TIER_BADGE = {
  1: { label: "TIER 1 · IN-CITY",       cls: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40" },
  2: { label: "TIER 2 · REGIONAL",       cls: "bg-sky-500/20 text-sky-300 border-sky-400/40" },
  3: { label: "TIER 3 · LONG-DISTANCE",  cls: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/40" },
  4: { label: "TIER 4 · DYNAMIC",        cls: "bg-amber-500/20 text-amber-300 border-amber-400/40" },
};

function LabeledField({ label, testid, children, hint }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 dark:text-zinc-400 block">
        {label}
      </label>
      {children}
      {hint && <div className="text-[10px] text-zinc-500 italic">{hint}</div>}
    </div>
  );
}

function Counter({ value, onChange, min = 0, max = 15, testid }) {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button" variant="outline" size="sm"
        onClick={() => onChange(Math.max(min, value - 1))}
        data-testid={`${testid}-dec`}
        className="h-9 w-9 p-0"
      >
        <Minus className="h-3.5 w-3.5"/>
      </Button>
      <Input
        type="number" min={min} max={max}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (Number.isFinite(n)) onChange(Math.max(min, Math.min(max, n)));
          else onChange(min);
        }}
        className="h-9 text-center font-bold w-16"
        data-testid={testid}
      />
      <Button
        type="button" variant="outline" size="sm"
        onClick={() => onChange(Math.min(max, value + 1))}
        data-testid={`${testid}-inc`}
        className="h-9 w-9 p-0"
      >
        <Plus className="h-3.5 w-3.5"/>
      </Button>
    </div>
  );
}

export default function NewTripPage() {
  const nav = useNavigate();
  const [rateTable, setRateTable] = useState(null);
  const [loadingTable, setLoadingTable] = useState(true);

  const [riderName,    setRiderName]    = useState("");
  const [riderPhone,   setRiderPhone]   = useState("");
  const [pickupAddr,   setPickupAddr]   = useState("");
  const [dropoffKey,   setDropoffKey]   = useState("weatherford-in-city");
  const [dropoffAddr,  setDropoffAddr]  = useState("");
  const [pax,          setPax]          = useState(1);
  const [roundTrip,    setRoundTrip]    = useState(false);
  const [extraStops,   setExtraStops]   = useState(0);
  const [waitMinutes,  setWaitMinutes]  = useState(0);
  const [fuelTier,     setFuelTier]     = useState(2);
  const [gasPrice,     setGasPrice]     = useState(3.69);
  const [miles,        setMiles]        = useState(0);

  const [quote,        setQuote]        = useState(null);
  const [quoting,      setQuoting]      = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [lastTripId,   setLastTripId]   = useState(null);
  const [smsingReceipt, setSmsingReceipt] = useState(false);

  // v2.7.65+E146 · Phase 3 · Discount selector state. `discountKind` is
  // an enum matching the spec dropdown; only "custom" reveals a value input.
  const [discountKind,   setDiscountKind]   = useState("none");
  const [discountValue,  setDiscountValue]  = useState("");  // number-as-string
  const [voucherCode,    setVoucherCode]    = useState("");
  // v2.7.65+E149 · Real-time CCVAP verify state.
  const [voucherStatus,  setVoucherStatus]  = useState(null);  // {valid, reason, ...}
  const [voucherBusy,    setVoucherBusy]    = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pricing/new-trip/rate-table");
        setRateTable(data);
      } catch (e) {
        toast.error("Failed to load rate table");
      } finally { setLoadingTable(false); }
    })();
  }, []);

  const selectedPreset = useMemo(() => {
    if (!rateTable) return null;
    if (dropoffKey === rateTable.dynamic_key) return { key: dropoffKey, label: rateTable.dynamic_label, tier: 4 };
    return rateTable.presets.find((p) => p.key === dropoffKey) || null;
  }, [dropoffKey, rateTable]);

  const isInCity = selectedPreset?.tier === 1;
  const isDynamic = dropoffKey === rateTable?.dynamic_key;

  const runQuote = useCallback(async () => {
    if (!rateTable) return;
    setQuoting(true);
    try {
      // v2.7.65+E146 · Phase 3 · Fold the optional custom-discount inputs
      // into the quote request. In-city / round-trip auto discounts stay
      // baked into the engine; this is a manual overlay on top.
      let discountKindPayload = null;
      let discountPct = 0, discountAmount = 0;
      if (discountKind === "voucher") {
        discountKindPayload = "Account voucher";
      } else if (discountKind === "custom_pct") {
        discountKindPayload = "Custom % discount";
        discountPct = parseFloat(discountValue) || 0;
      } else if (discountKind === "custom_amount") {
        discountKindPayload = "Custom $ discount";
        discountAmount = parseFloat(discountValue) || 0;
      }
      const { data } = await api.post("/pricing/new-trip/quote", {
        rider_name:      riderName || null,
        rider_phone:     riderPhone || null,
        pickup_address:  pickupAddr || null,
        dropoff_key:     dropoffKey,
        dropoff_address: dropoffAddr || null,
        passenger_count: pax,
        round_trip:      roundTrip,
        extra_stops:     extraStops,
        wait_minutes:    waitMinutes,
        fuel_tier:       fuelTier,
        gas_price:       gasPrice,
        one_way_miles:   miles,
        discount_kind:   discountKindPayload,
        discount_pct:    discountPct,
        discount_amount: discountAmount,
        voucher_code:    voucherCode || null,
      });
      setQuote(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Quote failed");
      setQuote(null);
    } finally { setQuoting(false); }
  }, [rateTable, riderName, riderPhone, pickupAddr, dropoffKey, dropoffAddr, pax, roundTrip, extraStops, waitMinutes, fuelTier, gasPrice, miles, discountKind, discountValue, voucherCode]);

  // Auto-quote when key inputs change
  useEffect(() => {
    const t = setTimeout(runQuote, 300);
    return () => clearTimeout(t);
  }, [runQuote]);

  const canSave = quote && quote.subtotal > 0 && !saving
    && !(discountKind === "voucher" && voucherCode.trim() && voucherStatus && !voucherStatus.valid);

  const createTrip = async () => {
    if (!quote) return;
    if (!riderName.trim()) { toast.error("Rider name is required"); return; }
    if (!pickupAddr.trim()) { toast.error("Pickup address is required"); return; }
    setSaving(true);
    try {
      // v2.7.65+E143 · Post directly into the existing /api/trips pipeline
      // so dispatch board + earnings ledger + compliance fees fire exactly
      // the same as legacy meter trips. The `breakdown` dict + `mode`
      // ("new-trip") lets accounting bucket these separately.
      const breakdownDict = {
        source:                "new-trip",
        tier:                  quote.tier,
        dropoff_key:           quote.dropoff_key,
        dropoff_label:         quote.dropoff_label,
        fare_before_surcharge: quote.fare_before_surcharge,
        fuel_surcharge:        quote.fuel_surcharge,
        line_items:            quote.breakdown,
        formula_note:          quote.formula_note,
        round_trip:            roundTrip,
        extra_stops:           extraStops,
        wait_minutes:          waitMinutes,
        fuel_tier:             isInCity ? null : fuelTier,
        gas_price:             isInCity ? null : gasPrice,
        one_way_miles:         miles || 0,
      };
      const payload = {
        mode:            "new-trip",
        origin:          pickupAddr.trim(),
        destination:     dropoffAddr.trim() || quote.dropoff_label,
        miles:           miles || 0,
        minutes:         waitMinutes || 0,
        passengers:      pax,
        stops:           extraStops,
        breakdown:       breakdownDict,
        subtotal:        quote.subtotal,
        total:           quote.subtotal,
        payment_method:  "cash",
        customer_name:   riderName.trim(),
        customer_phone:  riderPhone.trim(),
      };
      const { data: created } = await api.post("/trips", payload);
      toast.success(`Trip created · ${fmtUsd(quote.subtotal)} logged`);
      // v2.7.65+E145 · Show receipt CTA instead of leaving the page — the
      // driver can print, SMS the rider, or hop back to dashboard.
      setLastTripId(created?.id || null);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data || e.message;
      toast.error(`Save failed: ${typeof detail === "string" ? detail : JSON.stringify(detail)}`);
    } finally { setSaving(false); }
  };

  // v2.7.65+E149 · Debounced CCVAP real-time verify. Fires 400 ms after
  // the driver stops typing so the badge flips green / red inline.
  useEffect(() => {
    if (discountKind !== "voucher" || !voucherCode.trim()) {
      setVoucherStatus(null); return undefined;
    }
    const t = setTimeout(async () => {
      setVoucherBusy(true);
      try {
        const { data } = await api.post("/vouchers/verify", {
          serial_number: voucherCode.trim(),
        });
        setVoucherStatus(data);
      } catch (e) {
        setVoucherStatus({ valid: false, reason: e?.response?.data?.detail || "Verify failed" });
      } finally { setVoucherBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [voucherCode, discountKind]);


  const smsReceipt = async () => {
    if (!lastTripId) return;
    if (!riderPhone.trim()) { toast.error("No rider phone on file"); return; }
    setSmsingReceipt(true);
    try {
      const { data } = await api.post(`/trips/${lastTripId}/receipt/sms`, {
        to_phone: riderPhone.trim(),
      });
      if (data.ok) toast.success(`Receipt SMS sent to ${data.phone}`);
      else if (data.error === "twilio_not_configured") toast.info("Preview · SMS logged to outbox (DRY-RUN)");
      else toast.error(data.error || "SMS failed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS failed");
    } finally { setSmsingReceipt(false); }
  };

  if (loadingTable) {
    return (
      <Layout title="New Trip">
        <div className="p-8 flex items-center justify-center text-zinc-400">
          <Loader2 className="h-5 w-5 animate-spin mr-2"/> Loading rate table…
        </div>
      </Layout>
    );
  }

  const tierBadge = TIER_BADGE[selectedPreset?.tier || 4];

  return (
    <Layout title="New Trip">
      <div className="max-w-2xl mx-auto space-y-3 pb-24">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold">Upfront Dispatch</div>
          <h1 className="text-xl font-black text-slate-900 dark:text-zinc-100">New Trip</h1>
          <p className="text-xs text-zinc-500 mt-0.5">Enter passenger info to get an instant quote.</p>
        </div>

        {/* Rider */}
        <Card className="p-4 space-y-3" data-testid="new-trip-rider-card">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
            <User className="h-3 w-3"/>Rider
          </div>
          <LabeledField label="Rider name">
            <Input
              value={riderName} onChange={(e) => setRiderName(e.target.value)}
              placeholder="Jane Smith" data-testid="new-trip-name"
            />
          </LabeledField>
          <LabeledField label="Rider phone">
            <Input
              value={riderPhone} onChange={(e) => setRiderPhone(e.target.value)}
              placeholder="(405) 555-0199" type="tel" data-testid="new-trip-phone"
              inputMode="tel"
            />
          </LabeledField>
        </Card>

        {/* Trip */}
        <Card className="p-4 space-y-3" data-testid="new-trip-trip-card">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
            <MapPin className="h-3 w-3"/>Trip
          </div>
          <LabeledField label="Pickup address">
            <Input
              value={pickupAddr} onChange={(e) => setPickupAddr(e.target.value)}
              placeholder="Start location" data-testid="new-trip-pickup"
            />
          </LabeledField>
          <LabeledField label="Destination (preset)" hint="Pick the closest match. Choose 'Unlisted' for the dynamic 77-county formula.">
            <select
              value={dropoffKey} onChange={(e) => setDropoffKey(e.target.value)}
              className="w-full h-10 px-3 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-sm text-slate-900 dark:text-zinc-100"
              data-testid="new-trip-dropoff-key"
            >
              <optgroup label="Tier 1 · In-city">
                {rateTable.presets.filter((p) => p.tier === 1).map((p) => (
                  <option key={p.key} value={p.key}>{p.label} · ${p.flat_per_passenger.toFixed(2)}/pax</option>
                ))}
              </optgroup>
              <optgroup label="Tier 2 · Regional">
                {rateTable.presets.filter((p) => p.tier === 2).map((p) => (
                  <option key={p.key} value={p.key}>{p.label} · ${p.base_fare.toFixed(0)} base</option>
                ))}
              </optgroup>
              <optgroup label="Tier 3 · Long-distance / metro">
                {rateTable.presets.filter((p) => p.tier === 3).map((p) => (
                  <option key={p.key} value={p.key}>{p.label} · ${p.base_fare.toFixed(0)} base</option>
                ))}
              </optgroup>
              <optgroup label="Tier 4 · Dynamic">
                <option value={rateTable.dynamic_key}>{rateTable.dynamic_label}</option>
              </optgroup>
            </select>
          </LabeledField>
          <LabeledField label="Drop-off address (optional)">
            <Input
              value={dropoffAddr} onChange={(e) => setDropoffAddr(e.target.value)}
              placeholder="Exact drop-off (auto-filled from preset if empty)"
              data-testid="new-trip-dropoff-addr"
            />
          </LabeledField>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="Passengers">
              <Counter value={pax} onChange={setPax} min={1} max={15} testid="new-trip-pax"/>
            </LabeledField>
            <LabeledField label="Round trip">
              <div className="flex items-center gap-2 h-9">
                <Switch checked={roundTrip} onCheckedChange={setRoundTrip} data-testid="new-trip-round-trip"/>
                <span className="text-xs text-zinc-500">×1.75 (25 % off return)</span>
              </div>
            </LabeledField>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <LabeledField label="Extra stops" hint="$2.00 each">
              <Counter value={extraStops} onChange={setExtraStops} testid="new-trip-stops"/>
            </LabeledField>
            <LabeledField label="Wait minutes" hint="First 3 min free · $1.00/min after">
              <Counter value={waitMinutes} onChange={setWaitMinutes} max={240} testid="new-trip-wait"/>
            </LabeledField>
          </div>
        </Card>

        {/* Fuel — hidden for in-city Tier 1 */}
        {!isInCity && (
          <Card className="p-4 space-y-3" data-testid="new-trip-fuel-card">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">
              Fuel Surcharge
            </div>
            <LabeledField label="Fuel tier">
              <select
                value={fuelTier} onChange={(e) => setFuelTier(parseInt(e.target.value, 10))}
                className="w-full h-10 px-3 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-sm text-slate-900 dark:text-zinc-100"
                data-testid="new-trip-fuel-tier"
              >
                {rateTable.fuel_tiers.map((t) => (
                  <option key={t.tier} value={t.tier}>{t.label}</option>
                ))}
              </select>
            </LabeledField>
            <div className="grid grid-cols-2 gap-3">
              <LabeledField label="Gas price ($/gal)" hint={`Baseline $${rateTable.fuel_baseline.toFixed(2)}`}>
                <Input
                  type="number" step="0.01" min="0"
                  value={gasPrice}
                  onChange={(e) => setGasPrice(parseFloat(e.target.value) || 0)}
                  data-testid="new-trip-gas-price"
                />
              </LabeledField>
              <LabeledField label="One-way miles" hint={isDynamic ? "Required for dynamic formula" : "Optional"}>
                <Input
                  type="number" step="0.1" min="0"
                  value={miles}
                  onChange={(e) => setMiles(parseFloat(e.target.value) || 0)}
                  data-testid="new-trip-miles"
                />
              </LabeledField>
            </div>
          </Card>
        )}

        {/* v2.7.65+E146 · Phase 3 · Discount selector */}
        <Card className="p-4 space-y-3" data-testid="new-trip-discount-card">
          <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold">Discount</div>
          <LabeledField label="Discount type" hint="Auto-discounts (in-city 10%, round-trip 25%) apply automatically. Use this for extra promotions.">
            <select
              value={discountKind}
              onChange={(e) => { setDiscountKind(e.target.value); setDiscountValue(""); }}
              className="w-full h-10 px-3 rounded-md border border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-950 text-sm text-slate-900 dark:text-zinc-100"
              data-testid="new-trip-discount-kind"
            >
              <option value="none">Standard / None</option>
              <option value="auto_in_city" disabled>10 % Local In-City · Auto-applied</option>
              <option value="auto_round_trip" disabled>25 % Round-Trip · Auto-applied</option>
              <option value="voucher">Account voucher · custom code + amount</option>
              <option value="custom_pct">Custom discount · percent</option>
              <option value="custom_amount">Custom discount · fixed dollar</option>
            </select>
          </LabeledField>
          {(discountKind === "custom_pct" || discountKind === "custom_amount" || discountKind === "voucher") && (
            <div className="grid grid-cols-2 gap-3">
              <LabeledField label={discountKind === "custom_pct" ? "Discount %" : "Discount $"}>
                <Input
                  type="number" step="0.01" min="0"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  placeholder={discountKind === "custom_pct" ? "e.g. 15" : "e.g. 5.00"}
                  data-testid="new-trip-discount-value"
                />
              </LabeledField>
              {discountKind === "voucher" && (
                <LabeledField label="Voucher code" hint="Shown on the receipt line item.">
                  <Input
                    value={voucherCode}
                    onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                    placeholder="e.g. CCVAP-042"
                    data-testid="new-trip-voucher-code"
                  />
                </LabeledField>
              )}
            </div>
          )}
          {/* v2.7.65+E149 · Real-time CCVAP verify pill */}
          {discountKind === "voucher" && voucherCode.trim() && (
            <div
              data-testid="new-trip-voucher-status"
              data-voucher-valid={voucherStatus?.valid ? "true" : "false"}
              className={`text-xs px-3 py-2 rounded-md border flex items-center gap-2 ${
                voucherBusy
                  ? "border-zinc-700 bg-zinc-900 text-zinc-400"
                  : voucherStatus?.valid
                    ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-500"
                    : "border-rose-500/50 bg-rose-500/10 text-rose-400"
              }`}
            >
              {voucherBusy ? <><Loader2 className="h-3.5 w-3.5 animate-spin"/>Verifying…</>
                : voucherStatus?.valid ? (
                  <span data-testid="new-trip-voucher-valid">
                    ✓ Valid · {voucherStatus.rider_first_name || "rider"} ·
                    ${(voucherStatus.remaining_balance_usd || 0).toFixed(2)} available
                    {typeof voucherStatus.days_remaining === "number" ? ` · ${voucherStatus.days_remaining} d left` : ""}
                  </span>
                ) : (
                  <span data-testid="new-trip-voucher-invalid">
                    ✗ {voucherStatus?.reason || "Invalid or previously redeemed CCVAP voucher serial number"}
                  </span>
                )
              }
            </div>
          )}
          {discountKind === "voucher" && !discountValue && (
            <div className="text-[10px] text-amber-500 italic">Enter a $ amount to apply the voucher.</div>
          )}
        </Card>

        {/* Live quote */}
        <Card className="p-4 space-y-3 border-amber-400/40" data-testid="new-trip-quote-card">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1.5">
              <Calculator className="h-3 w-3"/>Live Quote
            </div>
            {tierBadge && (
              <span className={`text-[9px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${tierBadge.cls}`} data-testid="new-trip-tier-badge">
                {tierBadge.label}
              </span>
            )}
          </div>
          {quoting ? (
            <div className="flex items-center gap-2 text-xs text-zinc-500"><Loader2 className="h-3 w-3 animate-spin"/>Calculating…</div>
          ) : quote ? (
            <>
              <div className="space-y-1">
                {quote.breakdown.map((li, i) => (
                  <div key={i} className="flex items-center justify-between text-xs" data-testid={`quote-line-${i}`}>
                    <span className="text-zinc-600 dark:text-zinc-400 truncate mr-3">{li.label}</span>
                    <span className={`font-mono font-bold ${li.amount < 0 ? "text-emerald-500" : "text-slate-900 dark:text-zinc-100"}`}>
                      {li.amount < 0 ? "-" : ""}{fmtUsd(Math.abs(li.amount))}
                    </span>
                  </div>
                ))}
              </div>
              <div className="pt-2 border-t border-zinc-200 dark:border-zinc-800 flex items-center justify-between">
                <span className="text-sm uppercase tracking-widest font-bold text-slate-900 dark:text-zinc-100">Subtotal</span>
                <span className="text-2xl font-black text-amber-500" data-testid="new-trip-subtotal">{fmtUsd(quote.subtotal)}</span>
              </div>
              {quote.formula_note && (
                <div className="text-[10px] italic text-zinc-500">{quote.formula_note}</div>
              )}
            </>
          ) : (
            <div className="text-xs text-zinc-500">Fill in the fields above for a quote.</div>
          )}
        </Card>

        <Button
          className="w-full h-12 bg-amber-500 hover:bg-amber-400 text-black font-black text-base"
          onClick={createTrip}
          disabled={!canSave || !!lastTripId}
          data-testid="new-trip-submit"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2"/> : <Save className="h-4 w-4 mr-2"/>}
          {lastTripId ? "Trip saved" : `Create Trip ${quote ? `· ${fmtUsd(quote.subtotal)}` : ""}`}
          <ArrowRight className="h-4 w-4 ml-2"/>
        </Button>

        {lastTripId && (
          <Card className="p-4 space-y-2 border-emerald-500/40" data-testid="new-trip-saved-cta">
            <div className="text-[10px] uppercase tracking-widest text-emerald-500 font-bold">Trip saved ✓</div>
            <div className="text-xs text-zinc-500">Receipt link is ready to print or text.</div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                onClick={() => window.open(`/receipt/${lastTripId}`, "_blank")}
                data-testid="new-trip-view-receipt"
              >
                <Printer className="h-4 w-4 mr-1.5"/>View / print receipt
              </Button>
              <Button
                variant="outline"
                onClick={smsReceipt}
                disabled={smsingReceipt || !riderPhone.trim()}
                data-testid="new-trip-sms-receipt"
              >
                {smsingReceipt ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> : <MessageSquare className="h-4 w-4 mr-1.5"/>}
                SMS receipt to rider
              </Button>
              <Button
                variant="ghost"
                onClick={() => nav("/dashboard")}
                data-testid="new-trip-goto-dashboard"
              >
                Back to dashboard
              </Button>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}
